import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import CascadingLocationSelector from '../../components/location/CascadingLocationSelector';

const PATIENT_TYPES = ['Walk-In', 'Cash PUP', 'Client', 'Special Walk-In', 'Camp / Outreach'];
const REPORT_DISPATCH_MODES = ['Email', 'Print', 'Both'];
const RECEIPT_TYPES = [
  { value: 'ItemWise', label: 'Item-Wise Detailed Breakdown' },
  { value: 'TotalAmount', label: 'Consolidated Total Only' }
];

const INITIAL_FORM = {
  panelId: 0,
  panelCode: '',
  panelName: '',
  panelType: 'Cash PUP',
  centreId: null,
  discPercent: 0,
  paymentMode: 'Cash',
  rateSourceMode: 'self', // 'self' | 'inherit'
  referenceCodeOPD: 0,
  receiptType: 'ItemWise',
  hideRate: false,
  hideReceiptRate: false,
  hideDiscount: false,
  showAmtInBooking: true,
  reportDispatchMode: 'Email',
  contactMobile: '',
  contactEmail: '',
  reportEmail: '',
  address: '',
  city: 'New Delhi',
  state: 'Delhi',
  country: 'India',
  countryId: 1,
  stateId: 4,
  cityId: null,
  pinCodeId: null,
  pincode: '',
  isActive: true
};

export const PanelMasterPage = ({ showToast }) => {
  const navigate = useNavigate();

  // View state: 'form' | 'list'
  const [viewMode, setViewMode] = useState('form');
  const [isEditMode, setIsEditMode] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // List State
  const [panels, setPanels] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Status Change Confirmation
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Load Initial Lookups
  const loadLookups = useCallback(async () => {
    try {
      const [centreRes, panelRes] = await Promise.all([
        masterService.getCentres({ isActive: true }),
        masterService.getPanels()
      ]);
      if (centreRes.success && centreRes.data) {
        setCentres(centreRes.data);
        if (!formData.centreId && centreRes.data.length > 0) {
          setFormData(prev => ({ ...prev, centreId: centreRes.data[0].centreId }));
        }
      }
      if (panelRes.success && panelRes.data) {
        setPanels(panelRes.data);
      }
    } catch (err) {
      console.error('Failed to load lookups', err);
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  // Load Panels for List
  const fetchPanels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterService.getPanels({
        search: searchQuery.trim() || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active'
      });
      if (res.success && res.data) {
        let list = res.data;
        if (categoryFilter) {
          list = list.filter(p => p.panelType?.toLowerCase() === categoryFilter.toLowerCase());
        }
        setPanels(list);
      }
    } catch (err) {
      showToast?.('Failed to load panels', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, statusFilter, showToast]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchPanels();
    }
  }, [viewMode, fetchPanels]);

  // Handle Form Change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Switch to clean Form
  const handleAddNew = () => {
    setFormData({
      ...INITIAL_FORM,
      centreId: centres[0]?.centreId || null,
      panelCode: `PNL_${Math.floor(100 + Math.random() * 900)}`
    });
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Switch to Edit Form
  const handleEditRow = (row) => {
    const isInherited = row.referenceCodeOPD > 0 && row.referenceCodeOPD !== (row.panelId || row.panel_ID);
    setFormData({
      panelId: row.panelId || row.panel_ID || 0,
      panelCode: row.panelCode || '',
      panelName: row.panelName || '',
      panelType: row.panelType || 'Cash PUP',
      centreId: row.centreId || centres[0]?.centreId || null,
      discPercent: row.discPercent || 0,
      paymentMode: 'Cash',
      rateSourceMode: isInherited ? 'inherit' : 'self',
      referenceCodeOPD: row.referenceCodeOPD || 0,
      receiptType: row.receiptType || 'ItemWise',
      hideRate: !!row.hideRate,
      hideReceiptRate: !!row.hideReceiptRate,
      hideDiscount: !!row.hideDiscount,
      showAmtInBooking: row.showAmtInBooking !== false,
      reportDispatchMode: row.reportDispatchMode || 'Email',
      contactMobile: row.contactMobile || '',
      contactEmail: row.contactEmail || '',
      reportEmail: row.reportEmail || row.contactEmail || '',
      address: row.address || '',
      city: row.city || 'New Delhi',
      state: row.state || 'Delhi',
      country: row.country || 'India',
      countryId: row.countryId || null,
      stateId: row.stateId || null,
      cityId: row.cityId || null,
      pinCodeId: row.pinCodeId || null,
      pincode: row.pincode || row.pinCode || '',
      isActive: row.isActive !== false
    });
    setErrors({});
    setIsEditMode(true);
    setViewMode('form');
  };

  // Form Validation
  const validate = () => {
    const errs = {};
    if (!formData.panelName.trim()) errs.panelName = 'Panel Name is required.';
    if (!formData.panelCode.trim()) errs.panelCode = 'Panel Code is required.';
    if (!formData.centreId) errs.centreId = 'Processing Centre is required.';
    if (formData.rateSourceMode === 'inherit' && (!formData.referenceCodeOPD || formData.referenceCodeOPD === 0)) {
      errs.referenceCodeOPD = 'Select a parent panel to inherit rates from.';
    }
    if (formData.discPercent < 0 || formData.discPercent > 100) {
      errs.discPercent = 'Discount must be between 0% and 100%.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        panelCode: formData.panelCode.trim().toUpperCase(),
        panelName: formData.panelName.trim(),
        paymentMode: 'Cash',
        referenceCodeOPD: formData.rateSourceMode === 'inherit' ? Number(formData.referenceCodeOPD) : 0,
        centreId: Number(formData.centreId),
        discPercent: Number(formData.discPercent) || 0
      };

      let res;
      if (isEditMode) {
        res = await masterService.updatePanel(formData.panelId, payload);
      } else {
        res = await masterService.createPanel(payload);
      }

      if (res.success) {
        showToast?.(`Cash panel ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
        setViewMode('list');
        fetchPanels();
      } else {
        showToast?.(res.message || 'Operation failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving cash panel.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Status Change
  const handleConfirmStatus = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const pId = confirmTarget.panelId || confirmTarget.panel_ID;
      const newStatus = !confirmTarget.isActive;
      const res = await masterService.updatePanelStatus(pId, newStatus);
      if (res.success) {
        showToast?.(`Panel ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
        setPanels(prev => prev.map(p => (p.panelId === pId || p.panel_ID === pId) ? { ...p, isActive: newStatus } : p));
      } else {
        showToast?.(res.message || 'Status update failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error updating status.', 'error');
    } finally {
      setStatusUpdating(false);
      setConfirmTarget(null);
    }
  };

  // Centralized Standardized Pagination
  const {
    page,
    pageSize,
    totalRecords,
    pagedItems: pagedPanels,
    setPage,
    setPageSize
  } = usePagination({
    items: panels,
    resetDeps: [searchQuery, categoryFilter, statusFilter]
  });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? 'Update Cash Panel' : 'Create Cash Panel') : 'Cash Panel Master'}
      masterName="Panel"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form' && !isEditMode) handleAddNew();
        else setViewMode(mode);
      }}
    >
      {/* ─── FULL-SCREEN FORM VIEW ─── */}
      {viewMode === 'form' && (
        <div className="glass-card fade-in">
          <form onSubmit={handleSubmit} noValidate>
            {/* Section 1: Account Identity */}
            <FormSection
              title="01. Identity & Account Details"
              iconColor="#4287f5"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Panel Code <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.panelCode ? 'border-danger' : ''}`}
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                    value={formData.panelCode}
                    onChange={(e) => handleChange('panelCode', e.target.value.toUpperCase())}
                    placeholder="e.g. CASH_PUP01"
                    disabled={isEditMode}
                    required
                  />
                  {errors.panelCode && <span className="error-message">{errors.panelCode}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Panel / Organization Name <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.panelName ? 'border-danger' : ''}`}
                    value={formData.panelName}
                    onChange={(e) => handleChange('panelName', e.target.value)}
                    placeholder="e.g. Apex Health Outreach Centre"
                    required
                  />
                  {errors.panelName && <span className="error-message">{errors.panelName}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Category / Panel Group</label>
                  <select
                    className="form-select"
                    value={formData.panelType}
                    onChange={(e) => handleChange('panelType', e.target.value)}
                  >
                    {PATIENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Primary Processing Centre <span className="form-req">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.centreId ? 'border-danger' : ''}`}
                    value={formData.centreId || ''}
                    onChange={(e) => handleChange('centreId', Number(e.target.value))}
                    required
                  >
                    <option value="">-- Select Processing Centre --</option>
                    {centres.map(c => <option key={c.centreId} value={c.centreId}>{c.centreName} ({c.centreCode})</option>)}
                  </select>
                  {errors.centreId && <span className="error-message">{errors.centreId}</span>}
                </div>
              </div>
            </FormSection>

            {/* Section 2: Tariff & Inheritance */}
            <FormSection
              title="02. Tariff & Pricing Schedule"
              iconColor="#10b981"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group col-span-2">
                  <label className="form-label">Rate Schedule Mode</label>
                  <div style={{ display: 'flex', gap: '2rem', marginTop: '0.4rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                      <input
                        type="radio"
                        name="rateSourceMode"
                        value="self"
                        checked={formData.rateSourceMode === 'self'}
                        onChange={() => {
                          handleChange('rateSourceMode', 'self');
                          handleChange('referenceCodeOPD', 0);
                        }}
                      />
                      <span><strong>Independent Tariff</strong> (Maintains its own rate list)</span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                      <input
                        type="radio"
                        name="rateSourceMode"
                        value="inherit"
                        checked={formData.rateSourceMode === 'inherit'}
                        onChange={() => handleChange('rateSourceMode', 'inherit')}
                      />
                      <span><strong>Inherit Tariff</strong> (Derive test rates from parent panel)</span>
                    </label>
                  </div>
                </div>

                {formData.rateSourceMode === 'inherit' && (
                  <div className="form-group col-span-2">
                    <label className="form-label">
                      Inherit Rates From Parent Panel <span className="form-req">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.referenceCodeOPD ? 'border-danger' : ''}`}
                      value={formData.referenceCodeOPD || ''}
                      onChange={(e) => handleChange('referenceCodeOPD', Number(e.target.value))}
                    >
                      <option value="">-- Select Parent Cash Panel --</option>
                      {panels
                        .filter(p => (p.panelId || p.panel_ID) !== formData.panelId)
                        .map(p => (
                          <option key={p.panelId || p.panel_ID} value={p.panelId || p.panel_ID}>
                            {p.panelName} ({p.panelCode})
                          </option>
                        ))}
                    </select>
                    {errors.referenceCodeOPD && <span className="error-message">{errors.referenceCodeOPD}</span>}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Agreed Discount (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    className={`form-input ${errors.discPercent ? 'border-danger' : ''}`}
                    value={formData.discPercent}
                    onChange={(e) => handleChange('discPercent', e.target.value)}
                  />
                  {errors.discPercent && <span className="error-message">{errors.discPercent}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Mode</label>
                  <input
                    type="text"
                    className="form-input"
                    value="CASH BILLING ONLY"
                    readOnly
                    disabled
                    style={{ fontWeight: 600, color: '#16a34a' }}
                  />
                </div>
              </div>
            </FormSection>

            {/* Section 3: Receipt Policies */}
            <FormSection
              title="03. Cash Receipt Policies & Printing"
              iconColor="#f59e0b"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Receipt Format</label>
                  <select
                    className="form-select"
                    value={formData.receiptType}
                    onChange={(e) => handleChange('receiptType', e.target.value)}
                  >
                    {RECEIPT_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Report Dispatch Mode</label>
                  <select
                    className="form-select"
                    value={formData.reportDispatchMode}
                    onChange={(e) => handleChange('reportDispatchMode', e.target.value)}
                  >
                    {REPORT_DISPATCH_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>

                <div className="form-group col-span-2" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.hideReceiptRate}
                      onChange={(e) => handleChange('hideReceiptRate', e.target.checked)}
                    />
                    <span><strong>Mask Test Rates on Patient Receipts</strong> (Do not print individual test amounts)</span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.hideDiscount}
                      onChange={(e) => handleChange('hideDiscount', e.target.checked)}
                    />
                    <span><strong>Mask Discount Breakdown</strong> (Hide commercial concessions on receipt)</span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.showAmtInBooking}
                      onChange={(e) => handleChange('showAmtInBooking', e.target.checked)}
                    />
                    <span><strong>Display Booking Amount</strong> (Show totals at front counter)</span>
                  </label>
                </div>
              </div>
            </FormSection>

            {/* Section 4: Contact & Communication */}
            <FormSection
              title="04. Contact & Address"
              iconColor="#ec4899"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Coordinator Mobile</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.contactMobile}
                    onChange={(e) => handleChange('contactMobile', e.target.value)}
                    placeholder="10-digit mobile number"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Coordinator Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.contactEmail}
                    onChange={(e) => handleChange('contactEmail', e.target.value)}
                    placeholder="coordinator@lab.com"
                  />
                </div>

                <div className="form-group col-span-2">
                  <CascadingLocationSelector
                    countryId={formData.countryId}
                    stateId={formData.stateId}
                    cityId={formData.cityId}
                    pinCode={formData.pincode}
                    address={formData.address}
                    countryName={formData.country}
                    stateName={formData.state}
                    cityName={formData.city}
                    onChange={(loc) => {
                      setFormData(prev => ({
                        ...prev,
                        country: loc.country,
                        countryId: loc.countryId,
                        state: loc.state,
                        stateId: loc.stateId,
                        city: loc.city,
                        cityId: loc.cityId,
                        pincode: loc.pinCode,
                        pinCodeId: loc.pinCodeId,
                        address: loc.address !== undefined ? loc.address : prev.address
                      }));
                    }}
                    showAddressInput={true}
                    showPinCodeInput={true}
                    layout="grid"
                    requiredFields={{ country: false, state: false, city: false }}
                  />
                </div>

                <div className="form-group col-span-2">
                  <label className="checkbox-label" style={{ marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                    />
                    <span><strong>Panel Active</strong> (Available at Patient Registration)</span>
                  </label>
                </div>
              </div>
            </FormSection>

            {/* Form Footer */}
            <div className="form-actions-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewMode('list')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ minWidth: '130px' }}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update Panel' : 'Save Panel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── FULL-SCREEN GRID / LIST VIEW ─── */}
      {viewMode === 'list' && (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          {/* Filter Bar */}
          <div className="filter-bar">
            <div className="filter-bar-left">
              <div style={{ position: 'relative', width: '320px', minWidth: '240px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '2.4rem' }}
                  placeholder="Search by code, panel name..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>

              <select
                className="form-select"
                style={{ width: '180px' }}
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Categories</option>
                {PATIENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>

              <select
                className="form-select"
                style={{ width: '150px' }}
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>

            <div className="filter-bar-right">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddNew}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>+ Add Cash Panel</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="master-table mvc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Sr.</th>
                  <th style={{ width: '130px' }}>Panel Code</th>
                  <th>Cash Panel / Organization</th>
                  <th style={{ width: '170px' }}>Processing Centre</th>
                  <th style={{ width: '130px' }}>Category</th>
                  <th style={{ width: '160px' }}>Rate Schedule</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Discount %</th>
                  <th style={{ width: '110px', textAlign: 'center' }}>Mode</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '160px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      Loading cash panels catalog...
                    </td>
                  </tr>
                ) : pagedPanels.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      No cash panels matching your criteria.
                    </td>
                  </tr>
                ) : (
                  pagedPanels.map((r, index) => {
                    const pId = r.panelId || r.panel_ID;
                    const isInherited = r.referenceCodeOPD && r.referenceCodeOPD !== pId && r.referenceCodeOPD !== 0;
                    const parent = isInherited ? panels.find(p => (p.panelId || p.panel_ID) === r.referenceCodeOPD) : null;
                    const centre = centres.find(c => c.centreId === r.centreId);

                    return (
                      <tr key={pId || index}>
                        <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                          {(page - 1) * pageSize + index + 1}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent)' }}>
                          {r.panelCode}
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.panelName}</span>
                            {r.address && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.address}</span>}
                          </div>
                        </td>
                        <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          {centre ? centre.centreName : 'Default Facility'}
                        </td>
                        <td>
                          <span style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: '4px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                            {r.panelType || 'Cash PUP'}
                          </span>
                        </td>
                        <td>
                          {isInherited ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: '#eff6ff',
                                color: '#1d4ed8',
                                border: '1px solid #bfdbfe'
                              }}
                              title={`Inherits from: ${parent?.panelName || `Panel #${r.referenceCodeOPD}`}`}
                            >
                              Inherited: {parent ? parent.panelCode : `#${r.referenceCodeOPD}`}
                            </span>
                          ) : (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: '#ecfdf5',
                                color: '#047857',
                                border: '1px solid #a7f3d0'
                              }}
                            >
                              Own Tariff
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center', fontFamily: 'monospace', fontWeight: 600 }}>
                          {r.discPercent > 0 ? `${r.discPercent}%` : '0%'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#16a34a' }}>
                            Cash Only
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`badge-status ${r.isActive ? 'active' : 'inactive'}`}>
                            {r.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{
                                padding: '3px 8px',
                                fontSize: '11.5px',
                                color: 'var(--accent)',
                                borderColor: 'var(--accent-light)',
                                backgroundColor: 'var(--accent-light)'
                              }}
                              onClick={() => navigate(`/masters/panel-rates?panelId=${pId}`)}
                              title="Configure Panel-Wise Rates"
                            >
                              Rates
                            </button>

                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ padding: '3px 8px', fontSize: '11.5px' }}
                              onClick={() => handleEditRow(r)}
                              title="Edit Panel"
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{
                                padding: '3px 8px',
                                fontSize: '11.5px',
                                color: r.isActive ? '#ef4444' : '#10b981'
                              }}
                              onClick={() => setConfirmTarget(r)}
                              title={r.isActive ? 'Deactivate Panel' : 'Activate Panel'}
                            >
                              {r.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Standardized LIMS Centralized Pagination */}
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirmTarget}
        title={confirmTarget?.isActive ? 'Deactivate Cash Panel' : 'Activate Cash Panel'}
        message={
          confirmTarget?.isActive
            ? `Are you sure you want to deactivate "${confirmTarget?.panelName}"?`
            : `Are you sure you want to activate "${confirmTarget?.panelName}"?`
        }
        confirmLabel={confirmTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.isActive ? 'danger' : 'primary'}
        loading={statusUpdating}
        onConfirm={handleConfirmStatus}
        onCancel={() => setConfirmTarget(null)}
      />
    </MasterPageWrapper>
  );
};

export default PanelMasterPage;
